/** Numbered value blocks: browse products, discover professionals, etc. */
import { useCallback, useRef, useState } from 'react';
import { cn } from '@rocco/utils/cn';
import { useInView } from 'react-intersection-observer';
import { useLocalisation } from '@archipro-website/localisation';
import { ScrollReveal } from './components/ScrollReveal';
import { HDEDesktopFrame } from './components/HDEDesktopFrame';
import { getHDEMarketPossessive } from './utils/hde-event-copy';

interface ValueBlockProps {
    number: number;
    label: string;
    description: string;
    isVisible?: boolean;
    circleDelay?: number;
}

const ValueBlock = ({
    number,
    label,
    description,
    isVisible = false,
    circleDelay = 0,
}: ValueBlockProps) => (
    <div
        className="gap-47.25 flex shrink-0 flex-col px-[3.125rem] py-[1.25rem]"
        style={{ width: '29.625rem', height: '29.625rem' }}
    >
        <div className="gap-5.25 flex flex-col">
            <p className="font-sans text-[1rem] font-medium leading-[1.15] text-black">
                {label}
            </p>
            <div
                className={cn(
                    'duration-400 flex h-[4rem] w-[4rem] items-center justify-center rounded-full border-[0.1175rem] border-[#27282a] transition-all',
                    isVisible ? 'scale-100 opacity-100' : 'scale-0 opacity-0',
                )}
                style={{
                    transitionTimingFunction:
                        'cubic-bezier(0.34, 1.56, 0.64, 1)',
                    transitionDelay: `${circleDelay}ms`,
                }}
            >
                <span className="font-sans text-[1.875rem] font-medium leading-[1.2] text-[#27282a]">
                    {number}
                </span>
            </div>
        </div>
        <p className="font-sans text-[1.625rem] font-medium leading-[1.2] tracking-[-0.0325rem] text-black">
            {description}
        </p>
    </div>
);

const ImageBlock = ({ src, alt }: { src: string; alt: string }) => (
    <img
        src={src}
        alt={alt}
        className="shrink-0 overflow-hidden object-cover"
        style={{
            width: '29.625rem',
            height: '29.625rem',
            backgroundColor: '#c4c4c4',
        }}
    />
);

interface HowItWorksProps {
    assets: { photo1: string; photo2: string };
    isMobile?: boolean;
}

export const HowItWorks = ({ assets, isMobile = false }: HowItWorksProps) => {
    const { ref: sectionRef, inView } = useInView({
        triggerOnce: true,
        threshold: 0.25,
    });
    const trackRef = useRef<HTMLDivElement>(null);
    const [activeSlide, setActiveSlide] = useState(0);
    const { country } = useLocalisation();
    const browseProductsDescription = `Discover the products architects love to use, from ${getHDEMarketPossessive(country)} leading brands.`;

    const handleTrackScroll = useCallback(() => {
        if (!trackRef.current || !trackRef.current.firstElementChild) {
            return;
        }
        const slideWidth =
            trackRef.current.firstElementChild.getBoundingClientRect().width;
        if (!slideWidth) {
            return;
        }
        setActiveSlide(Math.round(trackRef.current.scrollLeft / slideWidth));
    }, []);

    if (isMobile) {
        const slides = [
            {
                number: 1,
                label: 'Browse Products',
                description: browseProductsDescription,
                image: assets.photo1,
                alt: 'Browse products',
            },
            {
                number: 2,
                label: 'Discover Professionals',
                description: 'Connect with trusted Architects and Builders',
                image: assets.photo2,
                alt: 'Discover professionals',
            },
        ];

        return (
            <section
                ref={sectionRef}
                data-testid="how-it-works"
                className="relative w-full overflow-hidden bg-white py-[1.65rem]"
            >
                <div className="h-px w-full bg-[#dbdbdb]" />
                <div
                    ref={trackRef}
                    onScroll={handleTrackScroll}
                    className="scrollbar-hide hde-mobile-carousel mt-[3.125rem] snap-x snap-mandatory scroll-px-[1.5rem] overflow-x-auto overflow-y-hidden px-[1.5rem]"
                >
                    <div className="flex gap-[1.75rem]">
                        {slides.map((slide) => (
                            <div
                                key={slide.number}
                                className="w-[22.8rem] shrink-0 snap-start pt-1"
                            >
                                <p className="text-[1rem] font-medium leading-[1.2] tracking-[0.035em] text-black">
                                    {slide.label}
                                </p>
                                <div className="mt-[1.3rem] flex items-start justify-between">
                                    <div className="flex h-[4.25rem] w-[4.25rem] items-center justify-center rounded-full border border-[#27282a]">
                                        <span className="text-[1.875rem] font-medium leading-none text-[#27282a]">
                                            {slide.number}
                                        </span>
                                    </div>
                                    <img
                                        src={slide.image}
                                        alt={slide.alt}
                                        className="aspect-167/186 w-[11rem] object-cover"
                                    />
                                </div>
                                <p className="mt-[4.5rem] text-[1.75rem] font-medium leading-[1.2] tracking-[-0.02em] text-black">
                                    {slide.description}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
                {/* pagination dots */}
                <div className="mt-[2.5rem] flex justify-start gap-[0.375rem] px-[1.5rem]">
                    {slides.map((_, index) => (
                        <div
                            key={index}
                            className={cn(
                                'h-[0.5rem] w-[0.5rem] rounded-full transition-colors',
                                activeSlide === index
                                    ? 'bg-[#141414]'
                                    : 'bg-[#d9d9d9]',
                            )}
                        />
                    ))}
                </div>
            </section>
        );
    }

    return (
        <section
            ref={sectionRef}
            data-testid="how-it-works"
            className="relative w-full overflow-hidden bg-white pt-[10rem]"
        >
            <HDEDesktopFrame>
                {/* Divider line */}
                <div className="mx-[3.125rem] overflow-hidden">
                    <div
                        className={cn(
                            'h-px w-full origin-left bg-black/20',
                            inView && 'hde-line-draw',
                        )}
                    />
                </div>

                {/* Value blocks row */}
                <div className="mt-3 flex gap-1">
                    <div className="flex flex-row gap-1">
                        <ScrollReveal
                            animation="fade-up"
                            duration={500}
                            delay={200}
                        >
                            <ValueBlock
                                number={1}
                                label="Browse Products"
                                description={browseProductsDescription}
                                isVisible={inView}
                                circleDelay={300}
                            />
                        </ScrollReveal>
                        <ScrollReveal
                            animation="fade-up"
                            duration={500}
                            delay={350}
                        >
                            <ImageBlock
                                src={assets.photo1}
                                alt="Browse products"
                            />
                        </ScrollReveal>
                    </div>
                    <div className="flex flex-row gap-1">
                        <ScrollReveal
                            animation="fade-up"
                            duration={500}
                            delay={500}
                        >
                            <ValueBlock
                                number={2}
                                label="Discover Professionals"
                                description="Connect with trusted Architects and Builders"
                                isVisible={inView}
                                circleDelay={600}
                            />
                        </ScrollReveal>
                        <ScrollReveal
                            animation="fade-up"
                            duration={500}
                            delay={650}
                        >
                            <ImageBlock
                                src={assets.photo2}
                                alt="Discover professionals"
                            />
                        </ScrollReveal>
                    </div>
                </div>
            </HDEDesktopFrame>
        </section>
    );
};
